import { router } from "expo-router";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet } from "react-native";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { Spacer } from "../Spacer";
import { ButtonCircle } from "./ButtonCircle";
import { Pressable } from "./Pressable";
import { BodyText } from "./Text/BodyText";
import { BrandText } from "./Text/BrandText";
import { View } from "./View";
import type { PressableState } from "./Pressable";

const S = StyleSheet.create({
  myPetsCard: {
    backgroundColor: styleConstantsV2.colorPrimaryBg,
    paddingLeft: 16,
    paddingRight: 32,
    paddingVertical: 24,
    borderRadius: styleConstantsV2.borderRadius4,
  },
});

interface MyPetListCardProps {
  myPetsNames: string[];
}

export const MyPetListCard = observer(function MyPetListCard({
  myPetsNames,
}: MyPetListCardProps) {
  const [isPressed, setIsPressed] = useState(false);

  const formatter = useMemo(() => {
    return new Intl.ListFormat(intl.locale, {
      style: "long",
      type: "conjunction",
    });
  }, []);

  const resolveBackgroundColor = useCallback((state: PressableState) => {
    if (state.pressed) {
      return styleConstantsV2.colorPrimaryHoverLight;
    }
    if (state.hovered) {
      return styleConstantsV2.colorPrimaryHoverLight;
    }
    return styleConstantsV2.colorPrimaryBg;
  }, []);

  return (
    <Pressable
      onPressIn={() => {
        setIsPressed(true);
      }}
      onPressOut={() => {
        setIsPressed(false);
      }}
      style={(state: PressableState) => {
        return [
          S.myPetsCard,
          {
            backgroundColor: resolveBackgroundColor(state),
          },
        ];
      }}
      onPress={async () => {
        router.navigate("/(app)/my-pets");
      }}
    >
      <View flexDirectionRow>
        <View flex>
          <BrandText sizeH3 weightBold colorBlack>
            <FormattedMessage
              id="E3FP8J"
              defaultMessage="My pets"
              description="My pets text inside the card on home screen"
            />
          </BrandText>
          <Spacer small />
          <BodyText colorBlack sizeLarge weightRegular>
            {formatter.format(myPetsNames)}
          </BodyText>
        </View>

        <ButtonCircle
          iconName="chevron-right"
          size="small"
          pointerEvents="none"
          backgroundColor={
            isPressed
              ? styleConstantsV2.colorGrey20
              : styleConstantsV2.colorWhite
          }
        />
      </View>
      <Spacer medium />
    </Pressable>
  );
});
